import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { listItems, listMounts, type BrowseItem, type MountSummary } from './fileBrowserApi';
import '../documentEditor/DocumentOpenPickerPopup.css';
import './FileBrowserDestinationPickerPopup.css';

export type StorageFileOpenPickerSelection = {
  mountId: string;
  path: string;
  name: string;
  mode: MountSummary['mode'];
};

export type StorageFileOpenPickerPopupProps = {
  visible: boolean;
  title: string;
  /** Files the picker will list. Folders are always listed so the user can navigate. */
  accept: (fileName: string) => boolean;
  /** Hide mounts the user cannot write to, for flows that create or save a file. */
  writableOnly?: boolean;
  onClose: () => void;
  onOpen: (selection: StorageFileOpenPickerSelection) => void;
};

type Crumb = { label: string; path: string | null };

export function StorageFileOpenPickerPopup({
  visible,
  title,
  accept,
  writableOnly = false,
  onClose,
  onOpen
}: StorageFileOpenPickerPopupProps) {
  const popupOptions = useResponsivePopupOptions({ width: 860, height: 620 });
  const [mounts, setMounts] = useState<MountSummary[]>([]);
  const [mountId, setMountId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState('');
  const [items, setItems] = useState<BrowseItem[]>([]);
  const [selected, setSelected] = useState<BrowseItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeMount = useMemo(
    () => mounts.find((m) => m.id === mountId) ?? null,
    [mounts, mountId]
  );

  const crumbs = useMemo((): Crumb[] => {
    if (!activeMount) return [];
    const parts: Crumb[] = [{ label: activeMount.displayName, path: '' }];
    if (!folderPath) return parts;
    const segments = folderPath.split('/').filter(Boolean);
    let accum = '';
    for (const segment of segments) {
      accum = accum ? `${accum}/${segment}` : segment;
      parts.push({ label: segment, path: accum });
    }
    return parts;
  }, [activeMount, folderPath]);

  const loadFolder = useCallback(
    async (nextMountId: string, nextPath: string) => {
      setLoading(true);
      setError(null);
      setSelected(null);
      try {
        const next = await listItems(nextMountId, nextPath || undefined);
        const filtered = next
          .filter((item) => item.isDirectory || accept(item.name))
          .sort((a, b) => {
            if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
          });
        setItems(filtered);
        setMountId(nextMountId);
        setFolderPath(nextPath);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not list folder.');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [accept]
  );

  const loadMounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listMounts();
      const visible = writableOnly ? next.filter((mount) => mount.mode === 'ReadWrite') : next;
      setMounts(visible);
      setSelected(null);
      if (visible[0]) {
        await loadFolder(visible[0].id, '');
        return;
      }
      setMountId(null);
      setFolderPath('');
      setItems([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load storage mounts.');
    } finally {
      setLoading(false);
    }
  }, [loadFolder, writableOnly]);

  useEffect(() => {
    if (!visible) return;
    void loadMounts();
  }, [visible, loadMounts]);

  const openSelection = useCallback(() => {
    if (!mountId || !selected || selected.isDirectory) return;
    if (!activeMount) return;
    onOpen({ mountId, path: selected.path, name: selected.name, mode: activeMount.mode });
  }, [activeMount, mountId, onOpen, selected]);

  const onRowActivate = useCallback(
    (item: BrowseItem) => {
      if (item.isDirectory) {
        if (!mountId) return;
        void loadFolder(mountId, item.path);
        return;
      }
      setSelected(item);
    },
    [loadFolder, mountId]
  );

  const onCrumbClick = useCallback(
    (crumb: Crumb) => {
      if (!mountId || crumb.path === null) return;
      void loadFolder(mountId, crumb.path);
    },
    [loadFolder, mountId]
  );

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      showTitle
      title={title}
      showCloseButton
      dragEnabled={!popupOptions.fullScreen}
      hideOnOutsideClick={false}
      {...popupOptions}
      wrapperAttr={{ class: 'jarvis-doc-open-picker-popup' }}
      position={{ my: 'center', at: 'center', of: window }}
    >
      <div className="jarvis-dest-picker jarvis-doc-open-picker">
        {mounts.length > 1 ? (
          <div className="jarvis-dest-picker__mounts" role="group" aria-label="Storage locations">
            {mounts.map((mount) => (
              <button
                key={mount.id}
                type="button"
                className={`jarvis-dest-picker__mount${mount.id === mountId ? ' jarvis-dest-picker__mount--active' : ''}`}
                title={mount.displayName}
                onClick={() => void loadFolder(mount.id, '')}
              >
                {mount.displayName}
              </button>
            ))}
          </div>
        ) : null}

        {activeMount ? (
          <div className="jarvis-doc-open-picker__toolbar">
            <nav className="jarvis-doc-open-picker__crumbs" aria-label="Folder path">
              {crumbs.map((crumb, index) => (
                <span key={`${crumb.label}-${index}`}>
                  {index > 0 ? <span className="jarvis-doc-open-picker__crumb-sep">/</span> : null}
                  <button
                    type="button"
                    className="jarvis-doc-open-picker__crumb"
                    disabled={index === crumbs.length - 1}
                    onClick={() => onCrumbClick(crumb)}
                  >
                    {crumb.label}
                  </button>
                </span>
              ))}
            </nav>
          </div>
        ) : null}

        {error ? <p className="jarvis-doc-open-picker__error">{error}</p> : null}

        <div className="jarvis-doc-open-picker__list" role="listbox" aria-label={title}>
          {loading ? <p className="jarvis-doc-open-picker__muted">Loading…</p> : null}
          {!loading && mountId != null
            ? items.map((item) => {
                const selectedRow = selected?.path === item.path;
                return (
                  <button
                    key={item.path}
                    type="button"
                    role="option"
                    aria-selected={selectedRow}
                    className={
                      selectedRow
                        ? 'jarvis-doc-open-picker__row jarvis-doc-open-picker__row--selected'
                        : 'jarvis-doc-open-picker__row'
                    }
                    onClick={() => onRowActivate(item)}
                    onDoubleClick={() => {
                      if (item.isDirectory) onRowActivate(item);
                      else {
                        setSelected(item);
                        if (activeMount) {
                          onOpen({
                            mountId,
                            path: item.path,
                            name: item.name,
                            mode: activeMount.mode
                          });
                        }
                      }
                    }}
                  >
                    <span
                      className={`jarvis-doc-open-picker__row-icon dx-icon ${
                        item.isDirectory ? 'dx-icon-folder' : 'dx-icon-doc'
                      }`}
                    />
                    <span className="jarvis-doc-open-picker__row-name">{item.name}</span>
                  </button>
                );
              })
            : null}
          {!loading && !error && mountId == null && mounts.length === 0 ? (
            <p className="jarvis-doc-open-picker__empty">
              {writableOnly
                ? 'You do not have write access to any storage location.'
                : 'No storage mounts available.'}
            </p>
          ) : null}
          {!loading && !error && mountId != null && items.length === 0 ? (
            <p className="jarvis-doc-open-picker__empty">No matching files in this folder.</p>
          ) : null}
        </div>

        <div className="jarvis-doc-open-picker__footer">
          <Button text="Cancel" stylingMode="outlined" onClick={onClose} />
          <Button
            text="Open"
            type="default"
            disabled={!selected || selected.isDirectory || !mountId}
            onClick={openSelection}
          />
        </div>
      </div>
    </Popup>
  );
}
